import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import useCheapestOfTourV2Offer from 'hooks/TourV2/useCheapestOfTourV2Offer'
import useTourTags from 'hooks/TourV2/useTourTags'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import { sortBy } from 'lib/array/arrayUtils'
import type { LuxPlusLabelTagOptions } from 'lib/tours/tourUtils'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import LuxPlusMonthLabel from 'takeovers/luxPlusMonthTakeover/components/LuxPlusMonthLabel'
import { LUX_PLUS_MONTH_TAKEOVER_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import { SiteTakeoverCampaignTagLabel } from 'takeovers/utils/components/SiteTakeoverCampaignTagLabel'
import AgentHubAdditionalCommissionLabel from 'agentHub/components/AgentHubAdditionalCommissionLabel'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import TourLuxPlusCampaignTag from './TourLuxPlusCampaignTag'
import TourV2UrgencyLabel from './TourV2UrgencyLabel'

const KNOWN_TAGS_POSITIONS = new Map<App.UrgencyLabelType, { position: number, tagLabel?: ReactNode }>([
  ['takeover_promo_code', { position: 5 }],
  ['agenthub_exclusive', { position: 4 }],
  ['lux_plus_member_price', { position: 3 }],
  ['lux_plus_early_access', { position: 3 }],
  ['lux_plus_general_release', { position: 3 }],
  ['promo_spend_earn', { position: 1 }],
  ['sale', { position: 0 }],
  ['departuresale', { position: 0 }],
])

interface Props {
  className?: string
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary
  departureId?: string
  /**
   * Which lux plus labels do we want to include within these labels
   * price only will return only price related labels
   * access only will return only access related (i.e. member only/early access)
   * All will return all that are relevant
   * None will show none
   *
   * @default all
   */
  luxPlusLabels?: LuxPlusLabelTagOptions
}

function TourV2UrgencyTags(props: Props) {
  const {
    className,
    offer,
    luxPlusLabels,
    departureId,
  } = props
  const tags = useTourTags(offer, luxPlusLabels, departureId)

  const tagLabels = useMemo(() => {
    return sortBy(tags, tag => {
      return KNOWN_TAGS_POSITIONS.get(tag.type)?.position ?? 2
    }, 'desc')
  }, [tags])

  const {
    cheapestPurchasableOption,
  } = useCheapestOfTourV2Offer(offer)

  const options = useTourV2PurchasableOptionDetails(cheapestPurchasableOption)
  const memberSavings = options?.memberSavings
  const isLuxPlusMonthTakeoverEnabled = useCurrentSiteTakeover({ offer })?.id === LUX_PLUS_MONTH_TAKEOVER_ID

  return <LabelGroup className={className}>
    <TourLuxPlusCampaignTag offerId={offer.id} />
    {isLuxPlusMonthTakeoverEnabled && !!memberSavings && <LuxPlusMonthLabel type="member-price-offer" saveAmount={memberSavings} />}
    {offer.campaignTags?.map(campaignTag => <SiteTakeoverCampaignTagLabel key={campaignTag} campaignTagId={campaignTag} onOfferCard />)}
    <AgentHubAdditionalCommissionLabel offerId={offer?.id} />
    {tagLabels.map((label, index) => <TourV2UrgencyLabel key={index} offer={offer} label={label} />)}
    {offer.schedule?.end && <OfferUrgencyCountdownLabel
      tagPlace="tour-v2-urgency-tags"
      endDate={offer.schedule?.end}
    />}
  </LabelGroup>
}

export default TourV2UrgencyTags
